import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    TextField,
    Button,
    Alert,
    CircularProgress,
    Divider,
    Card,
    CardContent,
    FormHelperText,
    Radio,
    RadioGroup,
    FormControlLabel,
    FormControl,
    FormLabel,
    Tabs,
    Tab
} from '@mui/material';
import {
    Email as EmailIcon,
    CheckCircle as CheckIcon,
    Error as ErrorIcon,
    AlternateEmail as GoogleIcon,
    Send as SendIcon
} from '@mui/icons-material';
import axios from 'axios';

function TabPanel({ children, value, index, ...other }) {
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`provider-tabpanel-${index}`}
            aria-labelledby={`provider-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
        </div>
    );
}

function EmailSettings() {
    const [settings, setSettings] = useState({
        provider: 'gmail',
        smtp_host: 'smtp.gmail.com',
        smtp_port: 587,
        smtp_user: '',
        smtp2go_host: 'mail.smtp2go.com',
        smtp2go_port: 587,
        smtp2go_user: '',
        smtp2go_from_email: '',
        is_configured: false
    });
    
    const [formData, setFormData] = useState({
        provider: 'gmail',
        // Gmail fields
        smtp_user: '',
        smtp_password: '',
        // SMTP2GO fields
        smtp2go_user: '',
        smtp2go_password: '',
        smtp2go_from_email: ''
    });
    
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [testingGmail, setTestingGmail] = useState(false);
    const [testingSmtp2go, setTestingSmtp2go] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [activeTab, setActiveTab] = useState(0);

    useEffect(() => {
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        try {
            setLoading(true);
            const response = await axios.get('/api/email/settings');
            setSettings(response.data);
            setFormData({
                provider: response.data.provider || 'gmail',
                smtp_user: response.data.smtp_user || '',
                smtp_password: '',
                smtp2go_user: response.data.smtp2go_user || '',
                smtp2go_password: '',
                smtp2go_from_email: response.data.smtp2go_from_email || ''
            });
            setActiveTab(response.data.provider === 'smtp2go' ? 1 : 0);
        } catch (error) {
            setError('Failed to fetch email settings');
        } finally {
            setLoading(false);
        }
    };

    const handleTestGmail = async () => {
        try {
            setTestingGmail(true);
            setError('');
            setSuccess('');

            await axios.post('/api/email/settings/test-gmail', {
                smtp_user: formData.smtp_user,
                smtp_password: formData.smtp_password
            });
            setSuccess('Gmail SMTP connection successful!');
        } catch (error) {
            setError(error.response?.data?.error || 'Gmail connection test failed');
        } finally {
            setTestingGmail(false);
        }
    };

    const handleTestSmtp2go = async () => {
        try {
            setTestingSmtp2go(true);
            setError('');
            setSuccess('');

            await axios.post('/api/email/settings/test-smtp2go', {
                smtp2go_user: formData.smtp2go_user,
                smtp2go_password: formData.smtp2go_password,
                smtp2go_from_email: formData.smtp2go_from_email
            });
            setSuccess('SMTP2GO connection successful!');
        } catch (error) {
            setError(error.response?.data?.error || 'SMTP2GO connection test failed');
        } finally {
            setTestingSmtp2go(false);
        }
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            setError('');
            setSuccess('');

            await axios.post('/api/email/settings', formData);
            setSuccess(`${formData.provider.toUpperCase()} email settings saved successfully!`);
            fetchSettings(); // Refresh settings
        } catch (error) {
            setError(error.response?.data?.error || 'Failed to save settings');
        } finally {
            setSaving(false);
        }
    };

    const handleProviderChange = (event, newValue) => {
        const provider = newValue === 0 ? 'gmail' : 'smtp2go';
        setFormData({ ...formData, provider });
        setActiveTab(newValue);
        setError('');
        setSuccess('');
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" mt={4}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            <Typography variant="h4" gutterBottom>
                Email Settings
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
                Choose your email provider and configure SMTP settings for sending email offers.
                All agents will use the provider you select here.
            </Typography>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

            {/* Current Settings Status */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Box display="flex" alignItems="center" mb={2}>
                        <EmailIcon sx={{ mr: 1 }} />
                        <Typography variant="h6">Current Configuration</Typography>
                    </Box>
                    <Box display="flex" alignItems="center" mb={1}>
                        {settings.is_configured ? (
                            <>
                                <CheckIcon color="success" sx={{ mr: 1 }} />
                                <Typography color="success.main">
                                    Email sending is configured using {settings.provider?.toUpperCase() || 'Gmail'}
                                </Typography>
                            </>
                        ) : (
                            <>
                                <ErrorIcon color="error" sx={{ mr: 1 }} />
                                <Typography color="error.main">
                                    Email sending is not configured
                                </Typography>
                            </>
                        )}
                    </Box>
                    {settings.is_configured && (
                        <Typography variant="body2" color="text.secondary">
                            Active provider: {settings.provider?.toUpperCase() || 'Gmail'} | 
                            Sender: {settings.provider === 'smtp2go' ? settings.smtp2go_from_email : settings.smtp_user}
                        </Typography>
                    )}
                </CardContent>
            </Card>

            {/* Provider Configuration */}
            <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                    Email Provider Configuration
                </Typography>

                {/* Provider Selection Tabs */}
                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                    <Tabs value={activeTab} onChange={handleProviderChange}>
                        <Tab label="Gmail / Google Workspace" />
                        <Tab label="SMTP2GO" />
                    </Tabs>
                </Box>

                {/* Gmail Configuration */}
                <TabPanel value={activeTab} index={0}>
                    <Typography variant="subtitle1" gutterBottom>
                        Gmail / Google Workspace SMTP Settings
                    </Typography>
                    
                    <Box mb={3}>
                        <TextField
                            fullWidth
                            label="SMTP Host"
                            value={settings.smtp_host}
                            disabled
                            margin="normal"
                            helperText="Gmail SMTP host (locked)"
                        />
                        <TextField
                            fullWidth
                            label="SMTP Port"
                            value={settings.smtp_port}
                            disabled
                            margin="normal"
                            helperText="Gmail SMTP port (locked)"
                        />
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    <TextField
                        fullWidth
                        label="Gmail Account Email"
                        type="email"
                        value={formData.smtp_user}
                        onChange={(e) => setFormData({ ...formData, smtp_user: e.target.value })}
                        margin="normal"
                        required
                        helperText="Your Gmail or Google Workspace account email address"
                    />

                    <TextField
                        fullWidth
                        label="Gmail App Password"
                        type="password"
                        value={formData.smtp_password}
                        onChange={(e) => setFormData({ ...formData, smtp_password: e.target.value })}
                        margin="normal"
                        required
                        helperText="16-character App Password from your Google account"
                    />

                    <FormHelperText sx={{ mt: 2, mb: 2 }}>
                        <strong>How to get a Gmail App Password:</strong><br />
                        1. Go to your Google Account settings<br />
                        2. Enable 2-Step Verification if not already enabled<br />
                        3. Go to Security → App passwords<br />
                        4. Generate a new app password for "Mail"<br />
                        5. Copy the 16-character password here
                    </FormHelperText>

                    <Box display="flex" gap={2} mt={3}>
                        <Button
                            variant="outlined"
                            onClick={handleTestGmail}
                            disabled={!formData.smtp_user || !formData.smtp_password || testingGmail}
                            startIcon={testingGmail ? <CircularProgress size={20} /> : <CheckIcon />}
                        >
                            {testingGmail ? 'Testing...' : 'Test Gmail Connection'}
                        </Button>
                    </Box>
                </TabPanel>

                {/* SMTP2GO Configuration */}
                <TabPanel value={activeTab} index={1}>
                    <Typography variant="subtitle1" gutterBottom>
                        SMTP2GO Settings
                    </Typography>
                    
                    <Box mb={3}>
                        <TextField
                            fullWidth
                            label="SMTP Host"
                            value={settings.smtp2go_host}
                            disabled
                            margin="normal"
                            helperText="SMTP2GO host (locked)"
                        />
                        <TextField
                            fullWidth
                            label="SMTP Port"
                            value={settings.smtp2go_port}
                            disabled
                            margin="normal"
                            helperText="SMTP2GO port (locked)"
                        />
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    <TextField
                        fullWidth
                        label="SMTP2GO Username"
                        value={formData.smtp2go_user}
                        onChange={(e) => setFormData({ ...formData, smtp2go_user: e.target.value })}
                        margin="normal"
                        required
                        helperText="Your SMTP2GO account username"
                    />

                    <TextField
                        fullWidth
                        label="SMTP2GO Password"
                        type="password"
                        value={formData.smtp2go_password}
                        onChange={(e) => setFormData({ ...formData, smtp2go_password: e.target.value })}
                        margin="normal"
                        required
                        helperText="Your SMTP2GO account password"
                    />

                    <TextField
                        fullWidth
                        label="From Email Address"
                        type="email"
                        value={formData.smtp2go_from_email}
                        onChange={(e) => setFormData({ ...formData, smtp2go_from_email: e.target.value })}
                        margin="normal"
                        required
                        helperText="Verified sender email address in your SMTP2GO account"
                    />

                    <FormHelperText sx={{ mt: 2, mb: 2 }}>
                        <strong>SMTP2GO Setup:</strong><br />
                        1. Sign up for a free SMTP2GO account<br />
                        2. Verify your sender email address<br />
                        3. Get your SMTP username and password from settings<br />
                        4. Enter your credentials here
                    </FormHelperText>

                    <Box display="flex" gap={2} mt={3}>
                        <Button
                            variant="outlined"
                            onClick={handleTestSmtp2go}
                            disabled={!formData.smtp2go_user || !formData.smtp2go_password || !formData.smtp2go_from_email || testingSmtp2go}
                            startIcon={testingSmtp2go ? <CircularProgress size={20} /> : <CheckIcon />}
                        >
                            {testingSmtp2go ? 'Testing...' : 'Test SMTP2GO Connection'}
                        </Button>
                    </Box>
                </TabPanel>

                {/* Save Button */}
                <Divider sx={{ my: 3 }} />
                <Box display="flex" justifyContent="center">
                    <Button
                        variant="contained"
                        size="large"
                        onClick={handleSave}
                        disabled={saving || (
                            formData.provider === 'gmail' ? 
                            (!formData.smtp_user || !formData.smtp_password) :
                            (!formData.smtp2go_user || !formData.smtp2go_password || !formData.smtp2go_from_email)
                        )}
                        startIcon={saving ? <CircularProgress size={20} /> : <EmailIcon />}
                    >
                        {saving ? 'Saving...' : `Save ${formData.provider === 'gmail' ? 'Gmail' : 'SMTP2GO'} Settings`}
                    </Button>
                </Box>
            </Paper>

            {/* Security Notice */}
            <Alert severity="info" sx={{ mt: 3 }}>
                <Typography variant="body2">
                    <strong>Security Notice:</strong> All passwords are encrypted using AES-256 encryption 
                    before being stored in the database. They are never stored in plain text and cannot be 
                    viewed after saving.
                </Typography>
            </Alert>
        </Box>
    );
}

export default EmailSettings;